import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import type { Discipline, MatchPayload, PlayerRatingChange } from "./tournament-match";
import { DISCIPLINE_LABEL, playedDisciplines, splitByDiscipline } from "./tournament-match";

export interface ProgressionRow {
  matchIndex: number;
  partner: string | null;
  opponents: string[];
  ratingBefore: number;
  ratingAfter: number;
  ratingChange: number;
  ratingNotCounted: boolean;
  result: "W" | "L" | "D";
  displayResult: string;
  displaySetScores: string[];
}

function findPlayerRatingChangeByName(
  match: MatchPayload,
  name: string,
): PlayerRatingChange | undefined {
  const changes = match.playerRatingChanges;

  if (!changes) {
    return undefined;
  }

  return Object.values(changes).find((entry) => entry?.name === name);
}

export function collectPlayerNames(
  matches: MatchPayload[],
  isDoubles: boolean,
): string[] {
  const names = new Set<string>();

  for (const match of matches) {
    if (isDoubles) {
      (match.team1 ?? []).forEach((player) => names.add(player));
      (match.team2 ?? []).forEach((player) => names.add(player));
      continue;
    }

    if (match.player1) {
      names.add(match.player1);
    }

    if (match.player2) {
      names.add(match.player2);
    }
  }

  return Array.from(names);
}

export function generateRatingProgression(
  matches: MatchPayload[],
  entityName: string,
  isDoubles: boolean,
): ProgressionRow[] {
  const entries: ProgressionRow[] = [];

  for (const match of matches) {
    let isEntity1 = false;
    let partner: string | null = null;
    let opponents: string[] = [];
    let ratingBefore = 0;
    let ratingAfter = 0;
    let ratingChange = 0;
    let ratingNotCounted = false;

    if (isDoubles) {
      const inTeam1 = Array.isArray(match.team1) && match.team1.includes(entityName);
      const inTeam2 = Array.isArray(match.team2) && match.team2.includes(entityName);

      if (!inTeam1 && !inTeam2) {
        continue;
      }

      isEntity1 = inTeam1;
      const playerTeam = isEntity1 ? match.team1 ?? [] : match.team2 ?? [];
      const opponentTeam = isEntity1 ? match.team2 ?? [] : match.team1 ?? [];
      partner = playerTeam.find((player) => player !== entityName) ?? null;
      opponents = opponentTeam;

      const playerRating = findPlayerRatingChangeByName(match, entityName);
      ratingBefore = Math.round(playerRating?.ratingBefore ?? 0);
      ratingAfter = Math.round(playerRating?.ratingAfter ?? 0);
      ratingChange = Math.round(playerRating?.ratingChange ?? 0);
      ratingNotCounted = !!playerRating?.ratingNotCounted;
    } else {
      if (match.player1 !== entityName && match.player2 !== entityName) {
        continue;
      }

      isEntity1 = match.player1 === entityName;
      const other = isEntity1 ? match.player2 : match.player1;
      opponents = other ? [other] : [];
      ratingBefore = Math.round(
        (isEntity1 ? match.player1RatingBefore : match.player2RatingBefore) ?? 0,
      );
      ratingAfter = Math.round(
        (isEntity1 ? match.player1RatingAfter : match.player2RatingAfter) ?? 0,
      );
      ratingChange = Math.round(
        (isEntity1 ? match.player1RatingChange : match.player2RatingChange) ?? 0,
      );
      ratingNotCounted = !!findPlayerRatingChangeByName(match, entityName)?.ratingNotCounted;
    }

    const matchResult = match.matchResult ?? "";
    const [sets1, sets2] = matchResult.split("-").map((n) => parseInt(n, 10));
    const setScores = Array.isArray(match.setScores) ? match.setScores : [];
    // A walkover carries a winner but no sets — its "0-0" must not read as a draw.
    const walkover = (match.winner === 1 || match.winner === 2) && setScores.length === 0;
    const result: "W" | "L" | "D" = (() => {
      if (match.winner === 1 || match.winner === 2) {
        return (match.winner === 1) === isEntity1 ? "W" : "L";
      }

      if (!matchResult || Number.isNaN(sets1) || Number.isNaN(sets2)) {
        return "D";
      }

      const own = isEntity1 ? sets1 : sets2;
      const other = isEntity1 ? sets2 : sets1;

      if (own > other) {
        return "W";
      }

      if (own < other) {
        return "L";
      }

      return "D";
    })();

    const displayResult = (() => {
      if (walkover) {
        return "wo";
      }

      if (!matchResult || Number.isNaN(sets1) || Number.isNaN(sets2)) {
        return matchResult;
      }

      return isEntity1 ? `${sets1}:${sets2}` : `${sets2}:${sets1}`;
    })();

    const displaySetScores = (() => {
      if (isEntity1) {
        return setScores;
      }

      return setScores.map((setScore) => {
        const [own, other] = String(setScore).split(":");

        return `${other}:${own}`;
      });
    })();

    entries.push({
      matchIndex: match.matchIndex ?? 0,
      partner,
      opponents,
      ratingBefore,
      ratingAfter,
      ratingChange,
      ratingNotCounted,
      result,
      displayResult,
      displaySetScores,
    });
  }

  return entries.sort((a, b) => a.matchIndex - b.matchIndex);
}

function ratingTierClass(rating: number): string {
  if (rating >= 2000) {
    return "rating-excellent";
  }

  if (rating >= 1800) {
    return "rating-very-good";
  }

  if (rating >= 1600) {
    return "rating-good";
  }

  if (rating >= 1400) {
    return "rating-average";
  }

  return "rating-beginner";
}

interface RatingProgressionPanelProps {
  matches: MatchPayload[];
  playerHref: (name: string, isDoubles: boolean) => string;
}

const RatingProgressionPanel: React.FC<RatingProgressionPanelProps> = ({
  matches,
  playerHref,
}) => {
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Discipline | null>(null);

  const byDiscipline = useMemo(() => splitByDiscipline(matches), [matches]);
  const available = useMemo(() => playedDisciplines(matches), [matches]);
  const activeDiscipline =
    picked && available.includes(picked) ? picked : available[0];

  const isDoubles = activeDiscipline === "debel";
  const disciplineMatches = activeDiscipline ? byDiscipline[activeDiscipline] : [];

  const playerNames = useMemo(
    () => collectPlayerNames(disciplineMatches, isDoubles),
    [disciplineMatches, isDoubles],
  );

  if (!activeDiscipline || playerNames.length === 0) {
    return null;
  }

  const visibleNames = playerNames.filter((name) =>
    name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>
        Zmiany rankingu
        {available.length === 1 && (
          <span style={{ fontWeight: 400, opacity: 0.7 }}>
            {" "}
            — {activeDiscipline}
          </span>
        )}
      </h3>

      {available.length > 1 && (
        <div className="tabs" role="tablist">
          {available.map((discipline) => (
            <button
              key={discipline}
              type="button"
              role="tab"
              aria-selected={activeDiscipline === discipline}
              className={`tab-button${activeDiscipline === discipline ? " active" : ""}`}
              onClick={() => setPicked(discipline)}
            >
              {DISCIPLINE_LABEL[discipline]} ({byDiscipline[discipline].length})
            </button>
          ))}
        </div>
      )}

      <div style={{ marginBottom: "1rem" }}>
        <input
          type="text"
          name="player-filter"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          placeholder="Szukaj zawodnika…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search-input"
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(400px, 100%), 1fr))",
          gap: "1rem",
        }}
      >
        {visibleNames.map((entityName) => {
          const progression = generateRatingProgression(
            disciplineMatches,
            entityName,
            isDoubles,
          );
          const tournamentChange = progression.reduce(
            (sum, entry) => sum + entry.ratingChange,
            0,
          );
          const lastAfter =
            progression.length > 0
              ? progression[progression.length - 1].ratingAfter
              : 0;

          return (
            <div
              key={entityName}
              className="card"
              style={{ padding: "0.75rem", margin: 0 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <h4 style={{ margin: 0 }}>
                  <Link to={playerHref(entityName, isDoubles)} className="player-link">
                    {entityName}
                  </Link>
                </h4>
                <div style={{ textAlign: "right" }}>
                  <div className={`rating-badge ${ratingTierClass(lastAfter)}`}>
                    {lastAfter || "—"}
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: tournamentChange >= 0 ? "green" : "red",
                      marginTop: "0.25rem",
                    }}
                  >
                    {tournamentChange >= 0 ? "+" : ""}
                    {tournamentChange}
                  </div>
                </div>
              </div>

              <div className="progression-list">
                {progression.map((entry, idx) => (
                  <ProgressionMatchRow
                    key={idx}
                    entry={entry}
                    isDoubles={isDoubles}
                    playerHref={playerHref}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface ProgressionMatchRowProps {
  entry: ProgressionRow;
  isDoubles: boolean;
  playerHref: (name: string, isDoubles: boolean) => string;
}

const ProgressionMatchRow: React.FC<ProgressionMatchRowProps> = ({
  entry,
  isDoubles,
  playerHref,
}) => {
  const deltaSign = entry.ratingChange >= 0 ? "+" : "";
  const deltaTone = entry.ratingChange >= 0 ? "progression-row__delta--up" : "progression-row__delta--down";

  return (
    <div className={`progression-row progression-row--${entry.result}`}>
      <span className="progression-row__result" aria-label={RESULT_LABEL[entry.result]}>
        {entry.result}
      </span>
      <div className="progression-row__body">
        <div className="progression-row__who">
          {entry.partner && (
            <span className="progression-row__partner">
              z{" "}
              <Link to={playerHref(entry.partner, isDoubles)} className="player-link">
                {entry.partner}
              </Link>
            </span>
          )}
          <span className="progression-row__vs">vs </span>
          {entry.opponents.map((opponent, index) => (
            <React.Fragment key={opponent}>
              <Link to={playerHref(opponent, isDoubles)} className="player-link">
                {opponent}
              </Link>
              {index < entry.opponents.length - 1 && " / "}
            </React.Fragment>
          ))}
        </div>
        <div className="progression-row__score">
          {entry.displayResult && (
            <span className="progression-row__sets">{entry.displayResult}</span>
          )}
          {entry.displaySetScores.length > 0 && (
            <span className="progression-row__points">{entry.displaySetScores.join(", ")}</span>
          )}
          {entry.ratingNotCounted && (
            <span className="progression-row__uncounted" title="Mecz nie liczony do rankingu">
              nie liczony
            </span>
          )}
        </div>
      </div>
      <div className="progression-row__rating">
        {entry.ratingNotCounted ? (
          <span className="progression-row__delta">—</span>
        ) : (
          <>
            <span className={`progression-row__delta ${deltaTone}`}>
              {deltaSign}
              {entry.ratingChange}
            </span>
            <span className="progression-row__range">
              {entry.ratingBefore} → {entry.ratingAfter}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

const RESULT_LABEL: Record<ProgressionRow["result"], string> = {
  W: "Wygrana",
  L: "Przegrana",
  D: "Remis",
};

export default RatingProgressionPanel;
